import React, { memo, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import Text from '../Common/NewText'
import LinkedCollection from '../Common/LinkedCollection'
import Disclaimer from './Disclaimer'
import StockImage from '@/stories/components/Cards/StockMiniCard/StockImage'
import LiveINStockPrice from '@/components/Stocks/LivePrices/LiveINStockPrice'
import {
  Candle,
  EntityMetadata,
  IMetaInfo,
  TimelineDatum,
} from '@/components/Stocks/Widgets/types'
import { IHeaders } from '@/components/Stocks/Pages/INStockDetailPage/types'
import { calcWatchlistState } from '@/components/watchlists/calcWatchlistState'
import LivePriceCommon from '@/components/Stocks/LivePrices/LivePriceCommon'
import { IND_STOCKS_DOMAIN } from '@/root/constants/common'

export interface OverviewProps {
  isStocks?: boolean
  short_name?: string
  name?: string
  symbol?: string
  logo?: string
  live_price?: string | number
  percent_change?: string | number
  companyCode?: string
  company_code?: string
  chartData?: Candle[]
  timeline_data?: TimelineDatum[] | null
  fallback?: string
  lineColor?: string
  firebase_path?: string
  showLoginWidget?: boolean
  showDematPersuasion?: boolean
  trading_symbol?: string
  candles_stick_navlink?: string
  graphLoading?: boolean
  loggedIn?: boolean
  isMobile?: boolean | null
  headers?: IHeaders
  metadata?: EntityMetadata
  isDemat?: boolean
  entityClass?: string
  meta_info?: IMetaInfo
  isGraphDisabled?: boolean | false
  last_updated?: string
  removeCurrency?: boolean
  linked_collections?: {
    title?: string
    cta?: string
  }[]
  description?: string
  exchange?: string
}

const ChartLoader = () => <div className="" />

// ssr:true here is to crawl the navlinks for SEO, charts are not rendered on server side
const DynamicChartWithNoSSR = memo(
  dynamic(() => import('./INStocksChart'), {
    ssr: true,
    loading: ChartLoader,
  }),
)

const WatchlistCTA = dynamic(
  () => import('@/components/watchlists/WatchlistCTA'),
  {
    ssr: false,
  },
)

const INStocksOverview = (props: OverviewProps) => {
  const {
    isStocks,
    short_name = '',
    name = '',
    symbol = '',
    logo = '',
    live_price = 0,
    percent_change = 0,
    fallback = '',
    firebase_path,
    showLoginWidget,
    showDematPersuasion,
    metadata,
    company_code,
    loggedIn,
    isMobile,
    isDemat,
    entityClass,
    meta_info = {},
    isGraphDisabled = false,
    last_updated,
    removeCurrency = false,
    linked_collections,
    description = '',
    exchange,
    ...rest
  } = props
  const { is_watchlisted, price_alert_metadata } = metadata ?? {}
  const [returnsData, setReturnsData] = useState<{
    absoluteReturn?: number
    absoluteReturnPercentage?: number
    returnTxt?: string
  }>({})
  const router = useRouter()

  const watchlist_state = calcWatchlistState(
    is_watchlisted ?? false,
    price_alert_metadata?.is_price_alerted ?? false,
  )

  return (
    <div>
      <section className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 md:gap-3">
          {logo && (
            <StockImage
              src={logo}
              alt={`${name} share price logo`}
              fallback={fallback}
            />
          )}

          <h1 className="flex items-baseline gap-x-2">
            {isStocks ? (
              <Text appearance="heading1">{short_name} Share Price </Text>
            ) : (
              <Text appearance="heading1">{name} </Text>
            )}
            {symbol && (
              <Text appearance="caption" addClassName="text-brand-grey-base">
                ({symbol})
              </Text>
            )}
          </h1>
        </div>

        {company_code && (entityClass === 'STOCK' || entityClass === 'ETF') && (
          <div>
            <WatchlistCTA
              state={watchlist_state}
              watchlist_id={`STOCK_${company_code}`}
              id2={rest.companyCode}
              source="indstock"
              loggedIn={loggedIn}
              kycStates={{
                indKycState: isDemat,
                usKycState: isDemat,
              }}
            />{' '}
          </div>
        )}
      </section>

      {!!linked_collections?.length && (
        <LinkedCollection linked_collections={linked_collections} />
      )}

      {description && (
        <Text appearance="caption" addClassName="text-brand-grey-base my-2.5">
          {description}
        </Text>
      )}

      <section className="my-2 flex flex-col gap-1">
        {meta_info.is_global ? (
          <LivePriceCommon
            price={live_price}
            percent_change={percent_change}
            socket_path={firebase_path}
            priceClassNames="text-base font-medium"
            percentClassNames="text-base font-medium"
            is_global={meta_info.is_global}
          />
        ) : (
          <LiveINStockPrice
            fallbackPrice={live_price}
            fallbackPercentChange={percent_change}
            firebase_path={firebase_path}
            returnsData={returnsData}
            fontSize="lg:text-2xl text-xl lg:leading-8 leading-5"
            removeCurrency={removeCurrency}
          />
        )}

        <div className="flex items-center gap-x-1.5">
          {last_updated && (
            <Text appearance="overline" addClassName="text-brand-grey-base">
              as on {dayjs(last_updated).format('hh:mmA, DD MMM YYYY')}
            </Text>
          )}
          {exchange && (
            <div className="flex items-center gap-x-1.5">
              <div className="bg-brand-grey-base h-[2px] w-[2px] rounded-full" />
              <Text appearance="overline" addClassName="text-brand-grey-base">
                {exchange}
              </Text>
            </div>
          )}
        </div>
      </section>

      {(showLoginWidget || showDematPersuasion) && !isMobile && (
        <Disclaimer
          onClick={() => {
            void router.push(
              `${IND_STOCKS_DOMAIN}/widget/page?page=KYC_FLOW&flowType=INDIAN_STOCKS`,
            )
          }}
        />
      )}

      {isGraphDisabled ? null : (
        <div className="h-[280px] md:h-[420px]">
          <DynamicChartWithNoSSR
            firebase_path={firebase_path}
            name={name}
            graphEndpointUrl={meta_info.graph_endpoint}
            showTechnicalCta={!meta_info.is_global}
            {...rest}
            isMobile={isMobile}
            setReturnsData={setReturnsData}
            is_global={meta_info.is_global}
            removeCurrency={removeCurrency}
          />
        </div>
      )}
    </div>
  )
}

export default INStocksOverview
